import React from 'react';
import { Input, Select, Tag, Typography } from 'antd';

import {
  SYNTHETIC_BOND_CURVE_TENOR_MONTHS,
  SyntheticBondGenerationDraft,
  SyntheticBondGenerationOptions,
} from 'state';
import FamilyMetadataForm from './FamilyMetadataForm';
import ReferenceCurveFamilyField from './ReferenceCurveFamilyField';
import { InfoLabel } from '../BondsPlayground/ModelGuide';

type Props = {
  disabled?: boolean;
  draft: SyntheticBondGenerationDraft;
  options?: SyntheticBondGenerationOptions;
  onCurrencyChange: (currencyId: number | null) => void;
  onDraftChange: (patch: Partial<SyntheticBondGenerationDraft>) => void;
  onMetadataChange: (
    field: keyof SyntheticBondGenerationDraft['family_metadata'],
    value: string | number | null,
  ) => void;
};

const CurveFamilyEditor: React.FC<Props> = ({
  disabled,
  draft,
  onCurrencyChange,
  onDraftChange,
  onMetadataChange,
  options,
}) => {
  const selectedSourceCount = SYNTHETIC_BOND_CURVE_TENOR_MONTHS.filter(
    tenor => draft.source_asset_ids_by_tenor_months[String(tenor)] !== null,
  ).length;

  return (
    <>
      <div className="synthetic-bonds__form-grid synthetic-bonds__form-grid--family">
        <label className="synthetic-bonds__field">
          <Typography.Text strong>
            <InfoLabel label="Currency family" title="One family per batch">
              Currency is shared by all source nodes and generated assets.
              Changing it clears the seven source assignments and family
              metadata so values cannot leak across families.
            </InfoLabel>
          </Typography.Text>
          <Select<number>
            allowClear
            disabled={disabled}
            options={(options?.currencies || []).map(currency => ({
              value: currency.id,
              label: currency.name
                ? `${currency.symbol} · ${currency.name}`
                : currency.symbol,
            }))}
            placeholder="Select currency"
            showSearch
            optionFilterProp="label"
            value={draft.currency_id ?? undefined}
            onChange={value => onCurrencyChange(value ?? null)}
          />
        </label>

        <ReferenceCurveFamilyField disabled={disabled} draft={draft} />

        <label className="synthetic-bonds__field">
          <Typography.Text strong>
            <InfoLabel label="Admin family label" title="Internal label only">
              Helps administrators recognize the batch. It is not copied into
              player-visible instrument labels and does not affect deterministic
              asset identity.
            </InfoLabel>
          </Typography.Text>
          <Input
            disabled={disabled}
            placeholder="e.g. US Treasury synthetic family"
            value={draft.family_label}
            onChange={event =>
              onDraftChange({ family_label: event.target.value })
            }
          />
        </label>
      </div>

      <div className="synthetic-bonds__source-summary">
        <div>
          <Typography.Text strong>
            Yield-curve source assignments
          </Typography.Text>
          <Typography.Paragraph type="secondary">
            Apply a saved reference family or choose the exact UnderlyingAsset
            for each tenor in the right sidecar. The slot label—not the asset
            name—is the reviewed tenor.
          </Typography.Paragraph>
        </div>
        <Tag color={selectedSourceCount === 7 ? 'success' : 'warning'}>
          {selectedSourceCount}/7 selected
        </Tag>
      </div>

      <FamilyMetadataForm
        disabled={disabled}
        metadata={draft.family_metadata}
        onChange={onMetadataChange}
        options={options}
      />
    </>
  );
};

export default CurveFamilyEditor;
